// frontend/src/components/FastReact/ImportSetupFios.jsx

import React, { useState, useRef } from 'react';
import apiClient, { fastReactAPI } from '../../api/client';
import { FiUpload, FiCheckCircle, FiAlertTriangle, FiX, FiEye } from 'react-icons/fi';
import './ImportSetupFios.css';

// ── Progress bar component ────────────────────────────────────────────────────
const ProgressBar = ({ current, total, processed, skipped }) => {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className="fios-progress-wrap">
      <div className="fios-progress-bar-track">
        <div className="fios-progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="fios-progress-stats">
        <span>{current} / {total} linhas</span>
        <span className="fios-pct">{pct}%</span>
      </div>
      <div className="fios-progress-counts">
        <span className="stat-ok">✅ {processed} processados</span>
        {skipped > 0 && <span className="stat-skip">⚠️ {skipped} ignorados</span>}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const ImportSetupFios = () => {
  const [file,        setFile]        = useState(null);
  const [phase,       setPhase]       = useState('idle'); // idle | uploading | streaming | done | error
  const [progress,    setProgress]    = useState({ current: 0, total: 0, processed: 0, skipped: 0 });
  const [lastRow,     setLastRow]     = useState('');
  const [result,      setResult]      = useState(null);
  const [preview,     setPreview]     = useState(null);
  const [previewFilter, setPreviewFilter] = useState({ opLinha: '', orderId: '', fios: '' });
  const [loadingPre,  setLoadingPre]  = useState(false);
  const fileRef  = useRef(null);
  const esRef    = useRef(null); // EventSource ref

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setResult(null); setPhase('idle'); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); setResult(null); setPhase('idle'); }
  };

  const clearFile = () => {
    // Cancel any running stream
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setFile(null); setResult(null); setPhase('idle');
    setProgress({ current: 0, total: 0, processed: 0, skipped: 0 });
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleImport = async () => {
    if (!file) return;

    // ── Step 1: upload file, get jobId ────────────────────────────────────
    setPhase('uploading');
    setResult(null);
    setProgress({ current: 0, total: 0, processed: 0, skipped: 0 });
    setLastRow('');

    let jobId, total;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fastReactAPI.uploadForImport(formData);
      if (!res.data.success) throw new Error(res.data.error || 'Erro no upload');
      jobId = res.data.jobId;
      total = res.data.total;
      if (total === 0) {
        setPhase('done');
        setResult({ success: true, total: 0, processed: 0, skipped: 0, errors: [], message: 'Ficheiro sem dados.' });
        return;
      }
    } catch (err) {
      setPhase('error');
      setResult({ success: false, error: err.response?.data?.error || err.message });
      return;
    }

    // ── Step 2: open SSE stream ────────────────────────────────────────────
    setPhase('streaming');
    setProgress({ current: 0, total, processed: 0, skipped: 0 });

    // Build SSE URL — EventSource can't send headers, token goes in query string
    // Use apiClient.defaults.baseURL so it always matches how axios reaches the backend
    const token   = localStorage.getItem('accessToken') || '';
    const apiBase = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
    const sseUrl  = `${apiBase}/fastreact/setup-fios/progress/${jobId}?token=${encodeURIComponent(token)}`;
    console.log('[SSE] connecting to:', sseUrl);

    const es = new EventSource(sseUrl);
    esRef.current = es;

    es.addEventListener('progress', (e) => {
      const d = JSON.parse(e.data);
      setProgress({ current: d.current, total: d.total, processed: d.processed, skipped: d.skipped });
      setLastRow(d.opLinha);
    });

    es.addEventListener('done', (e) => {
      const d = JSON.parse(e.data);
      setResult({ success: true, ...d });
      setPhase('done');
      es.close();
      esRef.current = null;
    });

    es.addEventListener('error', (e) => {
      let msg = 'Erro no streaming';
      try { msg = JSON.parse(e.data)?.message || msg; } catch {}
      setResult({ success: false, error: msg });
      setPhase('error');
      es.close();
      esRef.current = null;
    });

    es.onerror = () => {
      // SSE connection dropped (server closed normally or network error)
      if (phase !== 'done') {
        setPhase('error');
        setResult(r => r || { success: false, error: 'Ligação interrompida.' });
      }
      es.close();
      esRef.current = null;
    };
  };

  const handlePreview = async () => {
    setLoadingPre(true);
    try {
      const res = await fastReactAPI.previewSetupFios();
      setPreview(res.data.data || []);
    } catch { setPreview([]); }
    finally { setLoadingPre(false); }
  };

  const isRunning = phase === 'uploading' || phase === 'streaming';

  return (
    <div className="import-fios-page">
      <div className="page-header page-header--compact">
        <h2>Import Setup Fios Seamless</h2>
      </div>

      <div className="import-fios-card">
        <p className="import-fios-desc">
          Carregue um ficheiro Excel com as colunas <strong>OP-Linha</strong> (coluna A)
          e <strong>Fios</strong> (coluna B). A importação é processada linha a linha com
          progresso em tempo real.
        </p>

        {/* Drop zone */}
        <div
          className={`import-fios-dropzone ${file ? 'has-file' : ''} ${isRunning ? 'is-running' : ''}`}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => !file && !isRunning && fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xls"
            style={{ display: 'none' }} onChange={handleFileChange} />

          {file ? (
            <div className="import-fios-file-info">
              <FiCheckCircle size={20} style={{ color: '#059669', flexShrink: 0 }} />
              <span className="import-fios-filename">{file.name}</span>
              <span className="import-fios-filesize">({(file.size / 1024).toFixed(0)} KB)</span>
              {!isRunning && (
                <button type="button" className="import-fios-clear"
                  onClick={e => { e.stopPropagation(); clearFile(); }}>
                  <FiX size={14} />
                </button>
              )}
            </div>
          ) : (
            <div className="import-fios-placeholder">
              <FiUpload size={28} style={{ color: 'var(--gray-400)' }} />
              <p>Arraste um ficheiro Excel aqui ou clique para selecionar</p>
              <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>.xlsx ou .xls</p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="import-fios-actions">
          <button className="btn-primary"
            disabled={!file || isRunning}
            onClick={handleImport}>
            <FiUpload size={15} style={{ marginRight: 6 }} />
            {phase === 'uploading' ? 'A carregar ficheiro…'
              : phase === 'streaming' ? 'A importar…'
              : 'Importar'}
          </button>
          <button className="btn btn-secondary"
            disabled={loadingPre || isRunning}
            onClick={handlePreview}>
            <FiEye size={15} style={{ marginRight: 6 }} />
            {loadingPre ? 'A carregar…' : 'Ver valores actuais'}
          </button>
        </div>

        {/* Live progress */}
        {(phase === 'streaming' || (phase === 'uploading')) && (
          <div className="fios-progress-section">
            <div className="fios-progress-header">
              {phase === 'uploading'
                ? '⏳ A enviar ficheiro…'
                : `⚙️ A processar${lastRow ? `: ${lastRow}` : '…'}`}
            </div>
            <ProgressBar {...progress} />
          </div>
        )}

        {/* Result */}
        {result && (phase === 'done' || phase === 'error') && (
          <div className={`import-fios-result ${result.success ? 'result-ok' : 'result-err'}`}>
            {result.success ? (
              <>
                <div className="result-summary">
                  <FiCheckCircle size={18} style={{ color: '#059669' }} />
                  <strong>{result.message}</strong>
                </div>
                <div className="result-stats">
                  <span className="stat-ok">✅ {result.processed} processados</span>
                  {result.skipped > 0 && <span className="stat-skip">⚠️ {result.skipped} ignorados</span>}
                </div>
                {result.errors?.length > 0 && (
                  <div className="result-errors">
                    <p style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Linhas ignoradas:</p>
                    {result.errors.map((e, i) => (
                      <div key={i} className="result-error-row">
                        <FiAlertTriangle size={12} style={{ color: '#d97706', flexShrink: 0 }} />
                        <span><strong>{e.row}</strong> — {e.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="result-summary">
                <FiAlertTriangle size={18} style={{ color: '#dc2626' }} />
                <span>{result.error}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview panel */}
      {preview !== null && (
        <div className="import-fios-card" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0, fontSize: 14 }}>
              Valores actuais — FR_IMPETUS (UD_FIELD_ID = 91)
              <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--gray-500)', fontWeight: 400 }}>
                {preview.length} registos
              </span>
            </h4>
            <button type="button" className="import-fios-clear" onClick={() => { setPreview(null); setPreviewFilter({ opLinha: '', orderId: '', fios: '' }); }}>
              <FiX size={15} />
            </button>
          </div>
          {preview.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>Sem registos.</p>
          ) : (() => {
            const filtered = preview.filter(r =>
              (!previewFilter.opLinha || String(r.op_linha).toLowerCase().includes(previewFilter.opLinha.toLowerCase())) &&
              (!previewFilter.orderId || String(r.ORDER_ID).includes(previewFilter.orderId)) &&
              (!previewFilter.fios    || String(r.fios ?? '').toLowerCase().includes(previewFilter.fios.toLowerCase()))
            );
            return (
              <>
                {/* Filter row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr', gap: 8, marginBottom: 10 }}>
                  <input
                    type="text" placeholder="🔍 OP-Linha…"
                    className="fios-filter-input"
                    value={previewFilter.opLinha}
                    onChange={e => setPreviewFilter(f => ({ ...f, opLinha: e.target.value }))}
                  />
                  <input
                    type="text" placeholder="🔍 ORDER_ID…"
                    className="fios-filter-input"
                    value={previewFilter.orderId}
                    onChange={e => setPreviewFilter(f => ({ ...f, orderId: e.target.value }))}
                  />
                  <input
                    type="text" placeholder="🔍 Fios…"
                    className="fios-filter-input"
                    value={previewFilter.fios}
                    onChange={e => setPreviewFilter(f => ({ ...f, fios: e.target.value }))}
                  />
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 6 }}>
                  {filtered.length} de {preview.length} registos
                  {(previewFilter.opLinha || previewFilter.orderId || previewFilter.fios) && (
                    <button type="button" onClick={() => setPreviewFilter({ opLinha: '', orderId: '', fios: '' })}
                      style={{ marginLeft: 10, background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--primary)', fontSize: 11, textDecoration: 'underline' }}>
                      limpar filtros
                    </button>
                  )}
                </div>
                <div className="import-fios-table-wrap">
                  <table className="import-fios-table">
                    <thead>
                      <tr><th>OP-Linha</th><th>ORDER_ID</th><th>Fios (UD_FIELD_VALUE)</th></tr>
                    </thead>
                    <tbody>
                      {filtered.map((r, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.op_linha}</td>
                          <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>{r.ORDER_ID}</td>
                          <td>{r.fios || <em style={{ color: 'var(--gray-400)' }}>(vazio)</em>}</td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--gray-400)',
                          padding: '16px', fontStyle: 'italic' }}>
                          Sem resultados para os filtros aplicados.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ImportSetupFios;